import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  OpenInNew as ViewInGraphIcon,
  Email as EmailIcon,
  ChatBubble as MessageIcon,
  Description as DocumentIcon,
  Topic as TopicIcon,
} from '@mui/icons-material';
import { EvidenceItem, RelationshipStance } from '../../types/decision.types';

interface EvidenceCardProps {
  evidence: EvidenceItem;
  onViewInGraph?: (nodeUid: string) => void;
}

const stanceColors: Record<RelationshipStance, string> = {
  [RelationshipStance.SUPPORTING]: '#4DC4D2',
  [RelationshipStance.CONTRADICTING]: '#E94257',
  [RelationshipStance.NEUTRAL]: '#999999',
};

const confidenceLabel = (confidence: number): { label: string; color: 'success' | 'warning' | 'default' } => {
  if (confidence >= 0.7) return { label: 'High', color: 'success' };
  if (confidence >= 0.4) return { label: 'Medium', color: 'warning' };
  return { label: 'Low', color: 'default' };
};

const sourceTypeIcon = (type: string) => {
  switch (type) {
    case 'email': return <EmailIcon fontSize="small" />;
    case 'message': return <MessageIcon fontSize="small" />;
    case 'document': return <DocumentIcon fontSize="small" />;
    case 'topic': return <TopicIcon fontSize="small" />;
    default: return <DocumentIcon fontSize="small" />;
  }
};

const EvidenceCard: React.FC<EvidenceCardProps> = ({ evidence, onViewInGraph }) => {
  const stanceColor = stanceColors[evidence.stance];
  const conf = confidenceLabel(evidence.confidence);

  return (
    <Card
      style={{
        marginBottom: 12,
        borderLeft: `4px solid ${stanceColor}`,
        transition: 'box-shadow 0.2s ease-in-out',
      }}
    >
      <CardContent style={{ padding: 16 }}>
        <Stack spacing={1}>
          {/* Header: title + confidence badge */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center" style={{ flex: 1 }}>
              {sourceTypeIcon(evidence.sourceType)}
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {evidence.title}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                label={conf.label}
                size="small"
                color={conf.color}
                variant="outlined"
                style={{ height: 20, fontSize: '0.7rem' }}
              />
              <Typography variant="caption" color="text.secondary">
                {Math.round(evidence.confidence * 100)}%
              </Typography>
            </Stack>
          </Stack>

          {/* LLM Reasoning */}
          {evidence.reasoning && (
            <Typography
              variant="body2"
              style={{
                fontStyle: 'italic',
                color: stanceColor,
                backgroundColor: `${stanceColor}11`,
                paddingLeft: 12,
                paddingRight: 12,
                paddingTop: 6,
                paddingBottom: 6,
                borderRadius: 4,
                fontSize: '0.8rem',
              }}
            >
              {evidence.reasoning}
            </Typography>
          )}

          {/* Snippet */}
          <Typography
            variant="body2"
            color="text.secondary"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {evidence.snippet}
          </Typography>

          {/* Topics + View in Graph */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {evidence.topics.slice(0, 4).map(topic => (
                <Chip
                  key={topic}
                  label={topic}
                  size="small"
                  style={{
                    height: 20,
                    fontSize: '0.65rem',
                    backgroundColor: 'rgba(77, 196, 210, 0.1)',
                    color: '#4DC4D2',
                  }}
                />
              ))}
              {evidence.topics.length > 4 && (
                <Typography variant="caption" color="text.secondary">
                  +{evidence.topics.length - 4} more
                </Typography>
              )}
            </div>

            {onViewInGraph && (
              <Tooltip title="View in Knowledge Graph">
                <IconButton
                  size="small"
                  onClick={() => onViewInGraph(evidence.nodeUid)}
                  style={{ color: stanceColor }}
                >
                  <ViewInGraphIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EvidenceCard;
